import { Fuel } from 'lucide-react';
import { useCallback, useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { totalSobreTudo } from '@/componentes/totais';
import { HistoriaNaMesa } from '@/historias/mesa';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { useSessao } from '@/modulos/sessao/consultas';
import {
  formatarInteiro,
  formatarReais,
  interpretarDataHora,
  interpretarNumero,
  paraCampoDataHora,
} from '@/lib/formato';

import { comErroDe, conferirTodos, type ErrosDeCampo, marcaDeErro, NENHUM_ERRO, temErro } from './erros-de-campo';
import { Link } from '@tanstack/react-router';

import {
  explicarFalha,
  useCadastros,
  useAjustes,
  usePedirBalanco,
  useReceberNoTanque,
  useTanques,
  useTransferencias,
  useTransferirEntreTanques,
} from './consultas';
import type { SaldoDeTanque } from './tipos';
import { LivroDoTanque } from './LivroDoTanque';
import { mostrarLivroDoTanque } from './livro-do-tanque';
import { PedidosDeAjuste } from './PedidosDeAjuste';
import { TransferenciasEmTransito } from './TransferenciasEmTransito';
import { HubDoCombustivel } from './HubDoCombustivel';
import { AcoesNoRodape, JanelaComRodape } from './RodapeDaJanela';
import { destinosDaTransferencia, motivoParaNaoTransferir } from './transferencia';
import { opcaoDaMaquina, type MaquinaDaEscolha } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * Tanques e volume — a ferramenta 1 do módulo 08.
 *
 * ## A pergunta que esta tela responde
 *
 * "Quanto o livro diz que tem em cada tanque, e o que está esperando
 * aprovação para mudar isso?" O volume é o do livro. A "Última medição" e a
 * "Diferença" saíram a pedido do dono (03/10/2026): a conferência agora é o
 * BALANÇO, que não só relata a divergência — acerta o livro, depois de
 * aprovado por quem tem alçada de ajuste (0059).
 *
 * ## Uma ação laranja por vez
 *
 * Na página, o laranja é o "Lançar abastecimento" do hub. Balanço, receber e
 * transferir abrem em janela, por botões de contorno; dentro da janela, que
 * escurece e bloqueia a página atrás (R01, p.29), a ação dela é a laranja e
 * "Cancelar" é terciário à esquerda (p.15) — o mesmo desenho nas três.
 */

/** As tres acoes que uma linha de tanque dispara. */
type AcaoDeTanque = 'balanco' | 'recebimento' | 'transferencia';

const COLUNAS: readonly Coluna<SaldoDeTanque>[] = [
  {
    chave: 'tanque',
    rotulo: 'Tanque',
    /* Segunda linha em `TextoDaCelula` (p.18): nome longo sai com
       reticências e inteiro no `title`, e a linha fica nos 48 px. */
    celula: (t) => (
      <>
        <span className="codigo block text-tinta">{t.codigo}</span>
        <TextoDaCelula
          texto={`${t.nome}${t.padrao ? ' · padrão' : ''}`}
          classe="txt-apoio text-tinta-apagada"
          linhas={1}
        />
      </>
    ),
  },
  {
    chave: 'produto',
    rotulo: 'Produto',
    celula: (t) => (
      <>
        <TextoDaCelula texto={t.produto} classe="txt-corpo text-tinta" linhas={1} />
        <TextoDaCelula texto={t.obra ?? t.tipo} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </>
    ),
  },
  {
    chave: 'saldo',
    rotulo: 'Volume',
    historia: 'NUM-CMB-04',
    alinhamento: 'numero',
    celula: (t) => (
      <>
        <Medida valor={formatarInteiro(t.saldoCalculadoL)} unidade="L" />
        <span className="txt-linha2 block text-tinta-secundaria">
          {`de ${formatarInteiro(t.capacidadeL)}\u00a0L de capacidade`}
        </span>
      </>
    ),
    total: (linhas) => (
      <Medida valor={formatarInteiro(linhas.reduce((soma, t) => soma + t.saldoCalculadoL, 0))} unidade="L" />
    ),
  },
  {
    chave: 'preco',
    rotulo: 'Preço do litro',
    alinhamento: 'numero',
    /* Secundária (R01, p.28): sai abaixo de 1280 e dos cartões. A pergunta
       da tela é livro contra régua; o preço é do recebimento, e o formulário
       de lançamento continua dizendo a quanto sai o litro do tanque escolhido. */
    prioridade: 'secundaria',
    celula: (t) =>
      t.precoLitro === null ? (
        <span className="numero text-tinta-secundaria">Sem recebimento</span>
      ) : (
        <Medida valor={formatarReais(t.precoLitro)} unidade="/L" colado />
      ),
  },
];


export function PaginaTanques() {
  const consulta = useTanques();
  const cadastros = useCadastros();
  /* `?? []` nasce um array NOVO a cada render, e isso anularia o memo das
     colunas logo abaixo — elas seriam remontadas em toda renderizacao. */
  const tanques = useMemo(() => consulta.data ?? [], [consulta.data]);
  const paginaDosTanques = usePaginacao(tanques);

  const [escolhido, setEscolhido] = useState<string | null>(null);
  const tanque = tanques.find((t) => t.id === escolhido) ?? tanques[0] ?? null;
  /* As três ações sobre o tanque abrem em JANELA (pedido do dono, 25/09).
     Antes eram três formulários fixos ao lado da tabela, cada um com o seu
     botão laranja — três primárias na mesma tela. */
  const [acao, setAcao] = useState<AcaoDeTanque | null>(null);
  /* Receber, fazer balanço e transferir: cada ação só para quem tem a licença
     dela (prova do marco, 07/10/2026). Quem só lê vê os tanques e o livro. */
  const sessao = useSessao();
  const licencas = sessao.data?.permissoes ?? [];
  const podeReceber = licencas.includes('tanque.receber');
  const podeFazerBalanco = licencas.includes('tanque.balanco');
  const podeTransferir = licencas.includes('tanque.transferir');
  const podeCadastrarTanque = licencas.includes('combustivel.gerir');
  const fechar = () => {
    setAcao(null);
  };

  /* Abrir pela LINHA escolhe o tanque e abre a janela no mesmo gesto: sem
     isto, clicar "Fazer balanço" na terceira linha contaria o tanque da primeira. */
  const abrir = useCallback((tanqueId: string, qual: AcaoDeTanque) => {
    setEscolhido(tanqueId);
    setAcao(qual);
  }, []);

  /* Os dois números que a faixa do hub não diz (p.17), agora que a régua saiu
     da tela. "Acima da capacidade" é o defeito de 28/09 visto de fora: livro
     maior que o tanque é lançamento errado, e o caminho é corrigir. */
  const ajustes = useAjustes();
  const pendentes = (ajustes.data ?? []).filter((a) => a.situacao === 'aguardando aprovacao').length;
  const acimaDaCapacidade = tanques.filter((t) => t.saldoCalculadoL > t.capacidadeL).length;
  /* 0062: o que saiu de um tanque e ainda não chegou ao outro — não está em
     tanque nenhum, e a soma dos volumes não o conta. */
  const transferencias = useTransferencias();
  const emTransito = (transferencias.data ?? []).filter((t) => t.situacao === 'em_transito');
  const litrosEmTransito = emTransito.reduce((soma, t) => soma + t.quantidadeSaidaL, 0);

  return (
    <HubDoCombustivel>
      <FaixaIndicadores>
        <CardIndicador
          rotulo="Acima da capacidade"
          icone={Fuel}
          tom="parada"
          carregando={consulta.isPending}
          alerta={acimaDaCapacidade > 0}
          valor={consulta.isPending ? '' : formatarInteiro(acimaDaCapacidade)}
          contexto="tanques com volume acima da capacidade — corrija o lançamento errado"
        />
        <CardIndicador
          rotulo="Ajustes para aprovar"
          icone={Fuel}
          tom="standby"
          carregando={ajustes.isPending}
          valor={ajustes.isPending ? '' : formatarInteiro(pendentes)}
          contexto="correções e balanços esperando quem tem alçada de ajuste"
        />
        <CardIndicador
          rotulo="Em trânsito"
          icone={Fuel}
          tom="marca"
          carregando={transferencias.isPending}
          valor={transferencias.isPending ? '' : formatarInteiro(litrosEmTransito)}
          unidade="L"
          contexto={
            transferencias.isPending
              ? ''
              : emTransito.length === 0
              ? 'nenhuma transferência na estrada'
              : `em ${formatarInteiro(emTransito.length)} ${emTransito.length === 1 ? 'transferência esperando' : 'transferências esperando'} a chegada`
          }
        />
      </FaixaIndicadores>

      {/* As tres acoes sobre o tanque abrem pela LINHA, e nao por botao no topo
          (pedido do dono, 27/09). As janelas ficam montadas aqui, sem gatilho
          proprio: quem dispara e a coluna de acoes. */}
      <JanelaComRodape
        titulo={tanque === null ? 'Pedir balanço' : `Pedir balanço · ${tanque.nome}`}
        aberta={acao === 'balanco' && podeFazerBalanco}
        aoFechar={fechar}
      >
        <FormularioDeBalanco tanque={tanque} aoCancelar={fechar} />
      </JanelaComRodape>

      <JanelaComRodape
        titulo={tanque === null ? 'Lançar recebimento' : `Lançar recebimento · ${tanque.nome}`}
        aberta={acao === 'recebimento' && podeReceber}
        aoFechar={fechar}
      >
        <FormularioDeRecebimento
          tanque={tanque}
          aoCancelar={fechar}
          fornecedores={cadastros.data?.fornecedores ?? []}
          carregandoFornecedores={cadastros.isPending}
        />
      </JanelaComRodape>

      <JanelaComRodape
        titulo={tanque === null ? 'Transferir para outro tanque' : `Transferir de ${tanque.nome}`}
        aberta={acao === 'transferencia' && podeTransferir}
        aoFechar={fechar}
      >
        <FormularioDeTransferencia
          origem={tanque}
          tanques={tanques}
          equipamentos={cadastros.data?.equipamentos ?? []}
          carregandoEquipamentos={cadastros.isPending}
          aoCancelar={fechar}
        />
      </JanelaComRodape>

      <div>
        <div>
          <Tabela
            rotulo="Tanques e comboios"
            colunas={totalSobreTudo(COLUNAS, tanques)}
            linhas={paginaDosTanques.visiveis}
            rodape={<RodapeTabela {...paginaDosTanques.rodape} />}
            alturaDupla
            chaveDaLinha={(t) => t.id}
            carregando={consulta.isPending}
            selecionada={tanque?.id ?? null}
            aoSelecionar={(t) => {
              setEscolhido(t.id);
              mostrarLivroDoTanque();
            }}
            rotuloDoTotal="Soma"
            /* AS ACOES DA LINHA (p.18): ultima celula, a direita, a vista no
               hover e no foco — e FORA da conta das oito colunas, que e por
               que elas usam esta prop e nao uma coluna propria.

               DUAS direto e o teto: duas cabem sem espremer, a terceira comeca
               a competir com o dado. */
            {...(podeReceber
              ? {
                  acoes: (t: (typeof tanques)[number]) => (
                    <div className="flex items-center justify-end gap-2">
                      <Botao nivel="terciario" onClick={() => { abrir(t.id, 'recebimento'); }}>Receber</Botao>
                    </div>
                  ),
                }
              : {})}
            /* O "Mais ações" vira os tres pontos, fixos no canto direito da
               linha e sempre a vista (pedido do dono, 03/10/2026). */
            {...(podeFazerBalanco || podeTransferir
              ? {
                  menu: (t: (typeof tanques)[number]) => (
                    <MaisAcoes
                      forma="icone"
                      acoes={[
                        /* O balanço mora no menu, e não na linha (pedido do dono,
                           03/10): é conferência ocasional, e na linha fica só o
                           Receber, que é o lançamento do dia. */
                        ...(podeFazerBalanco ? [{ rotulo: 'Fazer balanço', aoEscolher: () => { abrir(t.id, 'balanco'); } }] : []),
                        ...(podeTransferir
                          ? [
                              {
                                rotulo: 'Transferir para outro tanque',
                                aoEscolher: () => { abrir(t.id, 'transferencia'); },
                                ...(motivoParaNaoTransferir(t, tanques) === null
                                  ? {}
                                  : { motivoIndisponivel: motivoParaNaoTransferir(t, tanques)! }),
                              },
                            ]
                          : []),
                      ]}
                    />
                  ),
                }
              : {})}
            erro={
              consulta.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar os tanques"
                  descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void consulta.refetch();
                      }}
                    >
                      Carregar os tanques
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              /* O texto honesto, e nao o convite impossivel.
                 Ele dizia "Cadastre o primeiro tanque" e NAO EXISTE caminho
                 para cadastrar tanque no sistema: nao ha rota POST
                 /combustivel/tanques, nao ha tela e nao ha botao. O estado
                 vazio mandava a pessoa para um lugar que nao existe, e o
                 formulario ao lado fechava o beco dizendo "Escolha um tanque
                 na tabela" — a tabela que acabara de dizer que esta vazia.
                 Enquanto a tela de cadastro nao existe (esta em
                 docs/PENDENCIAS.md), o estado vazio diz de quem e a saida. */
              /* ESTE TEXTO MENTIU POR DIAS: dizia que tanque so entrava pelo
                 banco depois de a tela de cadastro existir — a mesma cicatriz
                 que o CLAUDE.md conta sobre o PENDENCIAS. Corrigido em 22/09. */
              <EstadoVazio
                titulo="Nenhum tanque cadastrado"
                descricao={
                  podeCadastrarTanque
                    ? 'Cadastre o primeiro tanque ou comboio, com o produto que ele guarda, na tela de cadastros do combustível.'
                    : 'Quem cadastra tanque e comboio é o perfil Administrador, na tela de cadastros do combustível.'
                }
                acao={
                  podeCadastrarTanque ? (
                    <Link to="/combustivel/cadastros" className="botao botao-secundario">
                      Cadastrar tanque
                    </Link>
                  ) : undefined
                }
              />
            }
          />
        </div>
      </div>

      {/* O livro do tanque escolhido. Ele fica DEPOIS da tabela, e nao numa aba
          propria: a pergunta "por que este tanque esta com este saldo" nasce
          olhando a linha, e mandar a pessoa para outra tela para responde-la
          quebraria o fio no meio. */}
      {/* Os pedidos de ajuste ficam entre a tabela e o livro: quem aprova olha
          o pedido e, logo abaixo, o livro do tanque de que ele fala. */}
      <TransferenciasEmTransito />

      <PedidosDeAjuste />

      {tanque === null ? null : <LivroDoTanque tanqueId={tanque.id} tanqueNome={tanque.nome} />}
    </HubDoCombustivel>
  );
}

/**
 * O BALANÇO do tanque (0059) — a conferência que acerta o livro.
 *
 * A pessoa conta o que há no tanque e pede; quem tem alçada de ajuste aprova,
 * e só então o livro ganha a diferença, com a data da contagem. O volume do
 * livro NÃO é digitado: o banco o calcula no instante da conferência, e de
 * novo na aprovação — alguém pode lançar com data anterior no meio do caminho,
 * e o ajuste tem de levar o livro exatamente ao que foi contado.
 */
function FormularioDeBalanco({
  tanque,
  aoCancelar,
}: {
  readonly tanque: SaldoDeTanque | null;
  readonly aoCancelar: () => void;
}) {
  const base = useId();
  const pedir = usePedirBalanco();
  const [quando, setQuando] = useState(() => paraCampoDataHora(new Date()));
  const [volume, setVolume] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [pronto, setPronto] = useState<string | null>(null);

  const contado = interpretarNumero(volume);
  /* A estimativa usa o livro de HOJE; a conta que vale é a do banco, no
     instante da contagem. A frase abaixo do número diz isso. */
  const estimativa = tanque === null || contado === null ? null : contado - tanque.saldoCalculadoL;

  /* A mesma regra no `onBlur` e no envio (p.16). */
  function mensagemDe(campo: string): string | null {
    if (campo === 'quando') {
      const instante = interpretarDataHora(quando);
      if (instante === null) return 'Informe a data e a hora da conferência.';
      return instante.getTime() > Date.now() ? 'A conferência não pode estar no futuro. Informe quando o tanque foi contado.' : null;
    }
    if (campo === 'volume') {
      const valor = interpretarNumero(volume);
      if (valor === null || valor < 0) return 'Informe o volume contado no tanque, em litros.';
      if (tanque !== null && valor > tanque.capacidadeL) {
        return `O tanque comporta ${formatarInteiro(tanque.capacidadeL)}\u00a0L. Confira a contagem.`;
      }
      return null;
    }
    if (campo === 'motivo') {
      return motivo.trim().length < 15 ? 'Escreva o motivo com pelo menos 15 caracteres, dizendo o que a conferência achou.' : null;
    }
    return null;
  }

  function conferir(campo: string) {
    setErros((atual) => comErroDe(atual, campo, mensagemDe(campo)));
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setPronto(null);

    const instante = interpretarDataHora(quando);
    if (tanque === null) {
      setErros({ formulario: 'Escolha o tanque na tabela de tanques antes de pedir o balanço.' });
      return;
    }
    const conferidos = conferirTodos(['quando', 'volume', 'motivo'], mensagemDe);
    setErros(conferidos);
    if (temErro(conferidos) || instante === null || contado === null) return;

    pedir.mutate(
      {
        id: tanque.id,
        envio: { volumeContadoL: contado, contadoEm: instante.toISOString(), motivo: motivo.trim() },
        contexto: { alvo: { codigo: tanque.codigo, nome: tanque.nome } },
      },
      {
        /* Gravou, fecha (pedido do dono, 03/10/2026, no combustível): o aviso
           de sucesso da camada de chamadas diz o que entrou, e a janela
           aberta convidava a lançar de novo o que já foi lançado. */
        onSuccess: () => {
          aoCancelar();
        },
      },
    );
  }

  const falha = pedir.isError ? explicarFalha(pedir.error) : null;

  return (
    <form id={`${base}-formulario`} onSubmit={enviar} noValidate className="formulario-grade">
      {/* A recusa no TOPO do formulário (p.22). */}
      {erros.formulario ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {erros.formulario}
        </p>
      ) : null}
      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {falha.erro}. {falha.comoResolver}
        </p>
      ) : null}
      <div className="campo-linha flex flex-col gap-1">
        <p className="txt-apoio text-tinta-secundaria">
          {tanque === null
            ? 'Não há tanque cadastrado. Cadastre um tanque em Combustível › Cadastros para fazer o balanço dele.'
            : `Conferência de ${tanque.nome}. O volume hoje é de ${formatarInteiro(tanque.saldoCalculadoL)}\u00a0L, de ${formatarInteiro(tanque.capacidadeL)}\u00a0L de capacidade.`}
        </p>
      </div>

      <Campo
        id={`${base}-quando`}
        rotulo="Data e hora da conferência"
        ajuda="Veio do relógio do sistema. Troque se a contagem foi antes: o ajuste entra nos lançamentos nessa data."
        erro={erros.quando ?? null}
      >
        <input
          id={`${base}-quando`}
          type="datetime-local"
          value={quando}
          className="controle w-full"
          onChange={(evento) => {
            setQuando(evento.target.value);
            setErros((atual) => comErroDe(atual, 'quando', null));
          }}
          onBlur={() => {
            conferir('quando');
          }}
          {...marcaDeErro(erros, 'quando', `${base}-quando`)}
        />
      </Campo>

      <Campo id={`${base}-volume`} rotulo="Volume contado" erro={erros.volume ?? null}>
        <div className="flex items-center gap-2">
          <input
            id={`${base}-volume`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={volume}
            placeholder={tanque === null ? '800' : formatarInteiro(tanque.saldoCalculadoL)}
            className="controle w-full"
            onChange={(evento) => {
              setVolume(evento.target.value);
              setErros((atual) => comErroDe(atual, 'volume', null));
            }}
            onBlur={() => {
              conferir('volume');
            }}
            {...marcaDeErro(erros, 'volume', `${base}-volume`)}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
        </div>
      </Campo>

      {estimativa !== null && tanque !== null ? (
        <div className="bloco-rebaixado campo-linha flex flex-col gap-1 p-4">
          <span className="txt-legenda font-normal text-tinta-secundaria">Ajuste estimado</span>
          <span className="txt-secao text-tinta">
            {estimativa > 0 ? '+' : estimativa < 0 ? '−' : ''}
            {formatarInteiro(Math.abs(estimativa))} <span className="txt-corpo text-tinta-secundaria">L</span>
          </span>
          <span className="txt-apoio text-tinta-secundaria">
            Contra o volume de hoje. O valor que entra é calculado na aprovação, contra o volume no instante da conferência.
          </span>
        </div>
      ) : null}

      <div className="campo-linha min-w-0">
        <Campo id={`${base}-motivo`} rotulo="O que a conferência achou" erro={erros.motivo ?? null}>
          <input
            id={`${base}-motivo`}
            type="text"
            autoComplete="off"
            maxLength={300}
            value={motivo}
            placeholder="Régua da manhã: perda por evaporação desde a última conferência"
            className="controle w-full"
            onChange={(evento) => {
              setMotivo(evento.target.value);
              setErros((atual) => comErroDe(atual, 'motivo', null));
            }}
            onBlur={() => {
              conferir('motivo');
            }}
            {...marcaDeErro(erros, 'motivo', `${base}-motivo`)}
          />
        </Campo>
      </div>

      {pronto ? (
        <p role="status" className="campo-linha txt-apoio rounded-controle border border-borda bg-rebaixada px-4 py-2 text-tinta">
          {pronto}
        </p>
      ) : null}

      {/* No rodapé fixo da janela (R01, p.29): Cancelar à esquerda da ação. */}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <HistoriaNaMesa codigo="BTN-CMB-05" className="flex">
          <Botao
            nivel="primario"
            type="submit"
            form={`${base}-formulario`}
            disabled={pedir.isPending || tanque === null}
          >
            Pedir balanço
          </Botao>
        </HistoriaNaMesa>
      </AcoesNoRodape>
    </form>
  );
}

/**
 * O recebimento: a nota que entra no tanque.
 *
 * É ele que dá preço ao litro — e por isso o abastecimento interno não pergunta
 * preço a ninguém. Fornecedor é obrigatório porque o banco o exige: recebimento
 * sem fornecedor é combustível que entrou sem lastro, e o `check` da migração
 * recusa.
 */
function FormularioDeRecebimento({
  tanque,
  fornecedores,
  carregandoFornecedores,
  aoCancelar,
}: {
  readonly aoCancelar: () => void;
  readonly tanque: SaldoDeTanque | null;
  readonly fornecedores: readonly { readonly id: string; readonly nome: string }[];
  /** A lista ainda não chegou: o controle é esqueleto, e não uma lista vazia. */
  readonly carregandoFornecedores: boolean;
}) {
  const base = useId();
  const receber = useReceberNoTanque();
  const [quando, setQuando] = useState(() => paraCampoDataHora(new Date()));
  const [litros, setLitros] = useState('');
  const [preco, setPreco] = useState('');
  const [documento, setDocumento] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  /* A data nasce com o agora, e a marca diz isso até a pessoa trocá-la. */
  const [quandoTocado, setQuandoTocado] = useState(false);
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [pronto, setPronto] = useState<string | null>(null);

  /* O que ainda cabe, pela mesma conta da guarda do banco (0058): capacidade
     menos o saldo do livro. Arredonda para baixo — mostrar 2.500 L quando cabem
     2.499,5 L levaria a pessoa direto para a recusa. */
  const cabe = tanque === null ? null : Math.max(0, Math.floor(tanque.capacidadeL - tanque.saldoCalculadoL));
  const emLitros = (valor: number) => `${formatarInteiro(valor)}\u00a0L`;
  const ajudaDosLitros =
    tanque === null || cabe === null
      ? undefined
      : cabe > 0
        ? `Cabem até ${emLitros(cabe)}: o volume é de ${emLitros(tanque.saldoCalculadoL)}, de ${emLitros(tanque.capacidadeL)} de capacidade.`
        : `O volume já está na capacidade (${emLitros(tanque.saldoCalculadoL)} de ${emLitros(tanque.capacidadeL)}). Peça a correção do lançamento errado ou o balanço do tanque antes de receber.`;

  /* A mesma regra no `onBlur` e no envio (p.16). */
  function mensagemDe(campo: string): string | null {
    if (campo === 'fornecedor') {
      return fornecedor ? null : 'Escolha o fornecedor da nota. Sem ele o recebimento não tem lastro.';
    }
    if (campo === 'quando') {
      return interpretarDataHora(quando) === null ? 'Informe a data e a hora do recebimento.' : null;
    }
    if (campo === 'litros') {
      const valor = interpretarNumero(litros);
      if (valor === null || valor <= 0) return 'Informe quantos litros entraram no tanque.';
      if (cabe !== null && valor > cabe) {
        return `Cabem até ${emLitros(cabe)} neste tanque. Confira a quantidade da nota.`;
      }
      return null;
    }
    return null;
  }

  function conferir(campo: string) {
    setErros((atual) => comErroDe(atual, campo, mensagemDe(campo)));
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setPronto(null);

    const instante = interpretarDataHora(quando);
    const quantidade = interpretarNumero(litros);
    const precoLitro = interpretarNumero(preco);

    if (tanque === null) {
      setErros({ formulario: 'Escolha o tanque na tabela de tanques antes de lançar o recebimento.' });
      return;
    }
    const conferidos = conferirTodos(['fornecedor', 'quando', 'litros'], mensagemDe);
    setErros(conferidos);
    if (temErro(conferidos) || instante === null || quantidade === null) return;

    receber.mutate(
      {
        id: tanque.id,
        envio: {
          fornecedorId: fornecedor,
          quantidadeL: quantidade,
          ocorridoEm: instante.toISOString(),
          ...(precoLitro === null ? {} : { precoLitro }),
          ...(documento.trim() ? { documento: documento.trim() } : {}),
          motivo: 'recebimento de combustível pela tela',
        },
        contexto: {
          alvo: { codigo: tanque.codigo, nome: tanque.nome },
          fatos: [fornecedores.find((f) => f.id === fornecedor)?.nome ?? ''],
        },
      },
      {
        /* Gravou, fecha (pedido do dono, 03/10/2026, no combustível): o aviso
           de sucesso da camada de chamadas diz o que entrou, e a janela
           aberta convidava a lançar de novo o que já foi lançado. */
        onSuccess: () => {
          aoCancelar();
        },
      },
    );
  }

  const falha = receber.isError ? explicarFalha(receber.error) : null;

  return (
    <form id={`${base}-formulario`} onSubmit={enviar} noValidate className="formulario-grade">
      {/* A recusa no TOPO do formulário (p.22). */}
      {erros.formulario ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {erros.formulario}
        </p>
      ) : null}
      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {falha.erro}. {falha.comoResolver}
        </p>
      ) : null}
      <div className="campo-linha flex flex-col gap-1">
        <p className="txt-apoio text-tinta-secundaria">
          A nota que entra no tanque. É o preço dela que o abastecimento interno herda.
        </p>
      </div>

      <div className="campo-largo min-w-0">
        <Campo id={`${base}-fornecedor`} rotulo="Fornecedor" erro={erros.fornecedor ?? null}>
          {carregandoFornecedores ? (
            <ControleCarregando oQue="Carregando os fornecedores" />
          ) : (
            /* O seletor do sistema (p.23, p.31), com busca a partir de oito
               fornecedores; com um so, ele ja vem escolhido
               (`lib/escolha-unica.ts`). */
            <ListaDeEscolha
              id={`${base}-fornecedor`}
              rotulo="Fornecedor"
              valor={fornecedor}
              aoMudar={(valor) => {
                setFornecedor(valor);
                setErros((atual) => comErroDe(atual, 'fornecedor', null));
              }}
              aoSair={() => {
                conferir('fornecedor');
              }}
              rotuloVazio="Escolha o fornecedor"
              opcoes={fornecedores.map((f) => ({ valor: f.id, rotulo: f.nome }))}
              idDoErro={erros.fornecedor ? `${base}-fornecedor-erro` : undefined}
            />
          )}
        </Campo>
      </div>

      <Campo
        id={`${base}-quando`}
        rotulo="Data e hora"
        preenchido={!quandoTocado}
        erro={erros.quando ?? null}
        {...(quandoTocado ? {} : { ajuda: 'Agora. Troque se a nota chegou antes.' })}
      >
        <input
          id={`${base}-quando`}
          type="datetime-local"
          value={quando}
          className="controle w-full"
          onChange={(evento) => {
            setQuando(evento.target.value);
            setQuandoTocado(true);
            setErros((atual) => comErroDe(atual, 'quando', null));
          }}
          onBlur={() => {
            conferir('quando');
          }}
          {...marcaDeErro(erros, 'quando', `${base}-quando`)}
        />
      </Campo>

      <Campo
        id={`${base}-litros`}
        rotulo="Litros recebidos"
        erro={erros.litros ?? null}
        {...(ajudaDosLitros ? { ajuda: ajudaDosLitros } : {})}
      >
        <div className="flex items-center gap-2">
          <input
            id={`${base}-litros`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={litros}
            placeholder="1.000"
            className="controle w-full"
            onChange={(evento) => {
              setLitros(evento.target.value);
              setErros((atual) => comErroDe(atual, 'litros', null));
            }}
            onBlur={() => {
              conferir('litros');
            }}
            {...marcaDeErro(erros, 'litros', `${base}-litros`)}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
        </div>
      </Campo>

      <Campo
        id={`${base}-preco`}
        rotulo="Preço do litro"
        opcional
        ajuda={
          /* O preço da ÚLTIMA compra, como referência para conferir a nota
             (31/10/2026). Não preenche: preço é da nota, e o da última compra
             copiado sem olhar entraria no custo de todo abastecimento. */
          tanque?.precoLitro === null || tanque?.precoLitro === undefined
            ? 'Sem preço, os abastecimentos deste tanque entram sem custo.'
            : `Na última compra, ${formatarReais(tanque.precoLitro)} o litro. Sem preço, os abastecimentos entram sem custo.`
        }
      >
        <div className="flex items-center gap-2">
          <input
            id={`${base}-preco`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={preco}
            placeholder="6,14"
            className="controle w-full"
            onChange={(evento) => {
              setPreco(evento.target.value);
            }}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">/L</span>
        </div>
      </Campo>

      <Campo id={`${base}-documento`} rotulo="Documento" opcional>
        <input
          id={`${base}-documento`}
          type="text"
          autoComplete="off"
          value={documento}
          placeholder="NF 12345"
          className="controle w-full"
          onChange={(evento) => {
            setDocumento(evento.target.value);
          }}
        />
      </Campo>

      {pronto ? (
        <p role="status" className="campo-linha txt-apoio rounded-controle border border-borda bg-rebaixada px-4 py-2 text-tinta">
          {pronto}
        </p>
      ) : null}

      {/* Dentro da janela, a ação dela é a laranja: a janela bloqueia a página
          atrás, e o laranja do hub não se alcança com ela aberta. No rodapé
          fixo da janela (R01, p.29). */}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao
          nivel="primario"
          type="submit"
          form={`${base}-formulario`}
          disabled={receber.isPending || tanque === null}
        >
          Lançar recebimento
        </Botao>
      </AcoesNoRodape>
    </form>
  );
}

/**
 * A TRANSFERÊNCIA entre tanques — decisão 6 do dono. Carregar o comboio a partir
 * do tanque do pátio não é consumo: nenhuma máquina recebeu. A origem é o tanque
 * escolhido na tabela; o destino só oferece tanque do MESMO produto, e o banco
 * confere de novo.
 */
function FormularioDeTransferencia({
  origem,
  tanques,
  equipamentos,
  carregandoEquipamentos,
  aoCancelar,
}: {
  readonly aoCancelar: () => void;
  readonly origem: SaldoDeTanque | null;
  readonly tanques: readonly SaldoDeTanque[];
  /** Os candidatos a caminhão que leva (0062): a frota, com o código. */
  readonly equipamentos: readonly MaquinaDaEscolha[];
  /** A frota ainda não chegou: o controle é esqueleto, e não "Sem caminhão" sozinho. */
  readonly carregandoEquipamentos: boolean;
}) {
  const base = useId();
  const transferir = useTransferirEntreTanques();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const [destino, setDestino] = useState('');
  const [caminhao, setCaminhao] = useState('');
  /* Tanque ao lado (pátio abastecendo o comboio): chega na hora, sem estrada. */
  const [chegouJunto, setChegouJunto] = useState(false);
  const [litros, setLitros] = useState('');
  const [quando, setQuando] = useState(() => paraCampoDataHora(new Date()));
  const [quandoTocado, setQuandoTocado] = useState(false);
  /* Um erro POR CAMPO. Era uma string só, desenhada sempre debaixo de "Tanque
     que recebe" — mesmo quando o que faltava eram os litros ou a data. */
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [pronto, setPronto] = useState<string | null>(null);

  const destinos = destinosDaTransferencia(origem, tanques);
  if (origem === null) return null;
  /* A lista pode mudar com a janela aberta. Sem destino, a janela diz por quê
     em vez de ficar em branco. */
  if (destinos.length === 0) {
    return (
      <>
        <p className="txt-apoio text-tinta-secundaria">{motivoParaNaoTransferir(origem, tanques)}</p>
        <AcoesNoRodape>
          <Botao nivel="terciario" onClick={aoCancelar}>
            Cancelar
          </Botao>
        </AcoesNoRodape>
      </>
    );
  }

  /* A mesma regra no `onBlur` e no envio (p.16). */
  function mensagemDe(campo: string): string | null {
    if (campo === 'destino') return destino === '' ? 'Escolha o tanque que recebe.' : null;
    if (campo === 'litros') {
      const valor = interpretarNumero(litros);
      return valor === null || valor <= 0 ? 'Informe quantos litros foram transferidos.' : null;
    }
    if (campo === 'quando') {
      return interpretarDataHora(quando) === null ? 'Informe a data e a hora da transferência.' : null;
    }
    return null;
  }

  function conferir(campo: string) {
    setErros((atual) => comErroDe(atual, campo, mensagemDe(campo)));
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setPronto(null);
    const quantidade = interpretarNumero(litros);
    const instante = interpretarDataHora(quando);
    const conferidos = conferirTodos(['destino', 'litros', 'quando'], mensagemDe);
    setErros(conferidos);
    if (temErro(conferidos) || quantidade === null || instante === null) return undefined;
    if (origem === null) return undefined;
    transferir.mutate(
      {
        origemId: origem.id,
        destinoId: destino,
        ...(caminhao ? { caminhaoId: caminhao } : {}),
        quantidadeL: quantidade,
        ocorridoEm: instante.toISOString(),
        chegouJunto,
        motivo: 'transferência entre tanques pela tela',
        contexto: (() => {
          const para = tanques.find((t) => t.id === destino);
          return { alvo: { codigo: origem.codigo, nome: origem.nome }, ...(para ? { fatos: [`para ${para.codigo} · ${para.nome}`] } : {}) };
        })(),
      },
      {
        /* Gravou, fecha (pedido do dono, 03/10/2026, no combustível): o aviso
           de sucesso da camada de chamadas diz o que entrou, e a janela
           aberta convidava a lançar de novo o que já foi lançado. */
        onSuccess: () => {
          aoCancelar();
        },
      },
    );
    return undefined;
  }

  const falha = transferir.isError ? explicarFalha(transferir.error) : null;

  return (
    <form id={`${base}-formulario`} onSubmit={enviar} noValidate className="formulario-grade">
      {/* A recusa no TOPO do formulário (p.22). */}
      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {`${falha.erro}. ${falha.comoResolver}`}
        </p>
      ) : null}
      <div className="campo-linha flex flex-col gap-1">
        <p className="txt-apoio text-tinta-secundaria">
          {`Sai de ${origem.nome}, inclusive para outra sede. Só aparecem tanques do mesmo produto: transferir não é abastecer máquina. O combustível fica em trânsito até quem recebe registrar a chegada.`}
        </p>
      </div>
      <div className="campo-largo min-w-0">
        <Campo id={`${base}-destino`} rotulo="Tanque que recebe" erro={erros.destino ?? null}>
          {/* O seletor do sistema, com o volume e o que ainda cabe: é o que
              decide se a transferência cabe no destino. Com um destino só,
              ele já vem escolhido (`lib/escolha-unica.ts`). */}
          <ListaDeEscolha
            id={`${base}-destino`}
            rotulo="Tanque que recebe"
            valor={destino}
            aoMudar={(valor) => {
              setDestino(valor);
              setErros((atual) => comErroDe(atual, 'destino', null));
            }}
            aoSair={() => {
              conferir('destino');
            }}
            rotuloVazio="Escolha o tanque"
            opcoes={destinos.map((d) => ({
              valor: d.id,
              rotulo: d.nome,
              etiquetas: [{ texto: d.codigo, tom: 'neutro' as const, titulo: 'Código' }],
              detalhe: `${formatarInteiro(d.saldoCalculadoL)} L de ${formatarInteiro(d.capacidadeL)} L · cabem ${formatarInteiro(Math.max(0, Math.floor(d.capacidadeL - d.saldoCalculadoL)))} L`,
            }))}
            idDoErro={erros.destino ? `${base}-destino-erro` : undefined}
          />
        </Campo>
      </div>
      <div className="campo-largo min-w-0">
        <Campo
          id={`${base}-caminhao`}
          rotulo="Caminhão que leva"
          opcional
          ajuda="Deixe em branco quando não passa por caminhão, como o pátio que abastece o comboio ao lado."
        >
          {carregandoEquipamentos ? (
            <ControleCarregando oQue="Carregando a frota" />
          ) : (
            <ListaDeEscolha
              id={`${base}-caminhao`}
              rotulo="Caminhão que leva"
              valor={caminhao}
              aoMudar={setCaminhao}
              rotuloVazio="Sem caminhão"
              opcoes={comVisualDaFrota(equipamentos.map((e) => opcaoDaMaquina(e, { obra: false })))}
              adesivo
            />
          )}
        </Campo>
      </div>
      <Campo
        id={`${base}-litros`}
        rotulo="Litros que saíram"
        erro={erros.litros ?? null}
        ajuda={`${formatarInteiro(origem.saldoCalculadoL)} L em ${origem.nome}.`}
      >
        <div className="flex items-center gap-2">
          <input
            id={`${base}-litros`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={litros}
            placeholder="2.000"
            className="controle w-full"
            onChange={(evento) => {
              setLitros(evento.target.value);
              setErros((atual) => comErroDe(atual, 'litros', null));
            }}
            onBlur={() => {
              conferir('litros');
            }}
            {...marcaDeErro(erros, 'litros', `${base}-litros`)}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
        </div>
      </Campo>
      <Campo
        id={`${base}-quando`}
        rotulo="Data e hora da saída"
        preenchido={!quandoTocado}
        erro={erros.quando ?? null}
        {...(quandoTocado ? {} : { ajuda: 'Agora. Troque se saiu antes.' })}
      >
        <input
          id={`${base}-quando`}
          type="datetime-local"
          value={quando}
          className="controle w-full"
          onChange={(evento) => {
            setQuando(evento.target.value);
            setQuandoTocado(true);
            setErros((atual) => comErroDe(atual, 'quando', null));
          }}
          onBlur={() => {
            conferir('quando');
          }}
          {...marcaDeErro(erros, 'quando', `${base}-quando`)}
        />
      </Campo>
      <div className="campo-linha">
        <label className="flex items-center gap-2 txt-corpo text-tinta">
          <input
            type="checkbox"
            checked={chegouJunto}
            onChange={(evento) => {
              setChegouJunto(evento.target.checked);
            }}
          />
          Chegou junto, na mesma hora e sem perda (tanque ao lado)
        </label>
      </div>
      {pronto ? <p role="status" className="campo-linha txt-apoio text-tinta">{pronto}</p> : null}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <HistoriaNaMesa codigo="BTN-CMB-06" className="flex">
          <Botao nivel="primario" type="submit" form={`${base}-formulario`} disabled={transferir.isPending}>
            Transferir combustível
          </Botao>
        </HistoriaNaMesa>
      </AcoesNoRodape>
    </form>
  );
}
